import {
   LitElement,
   PropertyValues,
   TemplateResult,
   css,
   html,
   nothing,
} from 'lit';
import { customElement } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import { enumAttributeConverter, Sizes, SizesValuesMap } from '../enum-types';
import { getGlobalStyleSheets } from '../../global-styles';

import '../icon';
import '../placeholders';

import * as TagNames from './button.tagnames';

import { AtlAriaButtonProps, AtlButtonProps } from './button.props';
import * as ButtonEnums from './button.enums';
import { mergeJustifyContent } from '../justify-content';
import { IconSource } from '../icon/icon.props';
import { AtlIconTemplate } from '../icon/icon.component';

type ButtonType = 'button' | 'submit' | 'reset' | 'menu';

const ButtonTypeValues: { [key: string]: ButtonType } = {
   [ButtonEnums.ButtonType.Button]: 'button',
   [ButtonEnums.ButtonType.Submit]: 'submit',
   [ButtonEnums.ButtonType.Reset]: 'reset',
   [ButtonEnums.ButtonType.Menu]: 'menu',
};

type ITooltip = {
   dispose: () => void;
   update: () => void;

   _config: {
      title: string;
      placement: string;
   };
};

@customElement(TagNames.Button)
export class AtlButton extends LitElement implements AtlButtonProps {
   declare variant?: ButtonEnums.ButtonVariant;
   declare type?: ButtonEnums.ButtonType;
   declare size?: string;
   declare disabled?: boolean;
   declare fullWidth?: boolean;
   declare reverseOrder?: boolean;
   declare justifyContent?: ButtonEnums.JustifyContent;
   declare dataBsToggle?: ButtonEnums.ButtonDataBsToggle;
   declare dataBsPlacement?: ButtonEnums.ButtonDataBsPlacement;
   declare iconName?: string;
   declare iconSet?: ButtonEnums.IconSet;
   declare iconClassName?: string;
   declare iconSource?: IconSource;
   declare hiddenText?: string;
   declare buttonTooltip?: string;
   declare asChip?: boolean;
   declare buttonClasses?: string;
   declare buttonAriaSettings?: AtlAriaButtonProps;

   private _buttonTooltip: ITooltip | undefined;

   static properties = {
      variant: {
         type: String,
         converter: enumAttributeConverter(
            ButtonEnums.ButtonVariant,
            ButtonEnums.ButtonVariant.Cta
         ),
      },
      type: {
         type: String,
         converter: enumAttributeConverter(
            ButtonEnums.ButtonType,
            ButtonEnums.ButtonType.Button
         ),
      },
      size: { type: String },
      disabled: { type: Boolean },
      fullWidth: { type: Boolean },
      reverseOrder: { type: Boolean },
      justifyContent: {
         type: String,
         converter: enumAttributeConverter(ButtonEnums.JustifyContent),
      },
      dataBsToggle: {
         type: String,
         converter: enumAttributeConverter(ButtonEnums.ButtonDataBsToggle),
      },
      dataBsPlacement: {
         type: String,
         converter: enumAttributeConverter(ButtonEnums.ButtonDataBsPlacement),
      },
      iconName: { type: String },
      iconSet: {
         type: String,
         converter: enumAttributeConverter(
            ButtonEnums.IconSet,
            ButtonEnums.IconSet.Regular
         ),
      },
      iconClassName: { type: String },
      iconSource: {
         type: String,
         converter: enumAttributeConverter(IconSource),
      },
      hiddenText: { type: String },
      buttonTooltip: { type: String },
      asChip: { attribute: 'as-chip', type: Boolean },
      buttonClasses: { attribute: 'button-classes', type: String },
      buttonAriaSettings: { attribute: 'button-aria-settings', type: Object },
   };

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         :host {
            display: inline-block;
         }

         ::slotted(*) {
            display: flex;
            align-items: center;
            justify-content: center;
         }

         .xx-large {
            font-size: 1.5rem;
         }
      `,
   ];

   firstUpdated() {
      if (this.dataBsToggle != ButtonEnums.ButtonDataBsToggle.tooltip) return;
      this._initTooltip();
   }

   private _initTooltip() {
      if (!this.buttonTooltip) return;

      const button = this.shadowRoot?.querySelector('button') as HTMLElement;

      if (button) {
         this._buttonTooltip = new bootstrap.Tooltip(button, {
            boundary: document.body,
            trigger: 'hover',
            title: this.buttonTooltip,
            placement: this.dataBsPlacement || 'top',
            offset: '0, 6',
         });
      }
   }

   updated(changedProperties: PropertyValues) {
      const isTooltipButton =
         this.dataBsToggle === ButtonEnums.ButtonDataBsToggle.tooltip;

      if (!isTooltipButton && changedProperties.has('dataBsToggle')) {
         this._buttonTooltip?.dispose();
         this._buttonTooltip = undefined;
      }

      if (
         isTooltipButton &&
         (changedProperties.has('dataBsPlacement') ||
            changedProperties.has('buttonTooltip') ||
            changedProperties.has('dataBsToggle'))
      ) {
         this.updateTooltip();
      }
   }

   private updateTooltip() {
      if (!this._buttonTooltip) {
         this._initTooltip();
      }

      if (this._buttonTooltip) {
         this._buttonTooltip._config.placement = this.dataBsPlacement || 'top';
         this._buttonTooltip._config.title = this.buttonTooltip || '';
         this._buttonTooltip.update();
      }
   }

   render() {
      return AtlButtonTemplate({
         fullWidth: this.fullWidth,
         reverseOrder: this.reverseOrder,
         justifyContent: this.justifyContent,
         asChip: this.asChip,
         variant: this.variant,
         disabled: this.disabled,
         size: this.size,
         buttonClasses: this.buttonClasses,
         hiddenText: this.hiddenText,
         type: this.type,
         buttonAriaSettings: this.buttonAriaSettings,
         title: this.title,
         dataBsToggle: this.dataBsToggle,
         iconName: this.iconName,
         iconClassName: this.iconClassName,
         iconSource: this.iconSource,
         iconSet: this.iconSet,
         innerContent: html`<slot></slot>`,
         onClick:
            this.type == ButtonEnums.ButtonType.Submit
               ? this._handleSubmitClick
               : undefined,
      });
   }

   private _handleSubmitClick = () => {
      if (this.type !== ButtonEnums.ButtonType.Submit) return;

      const form = this.closest('form');
      if (!form) return;

      form.requestSubmit();
   };
}

type TButtonProps = AtlButtonProps & {
   innerContent?: TemplateResult;
   title?: string;
   onClick?: () => void;
};

export function AtlButtonTemplate(props: TButtonProps) {
   let btnClass = mergeJustifyContent(
      {
         'w-100': props.fullWidth === true,
         'flex-row-reverse': props.reverseOrder === true,
      },
      props.justifyContent
   );

   if (!props.asChip) {
      btnClass = {
         ...btnClass,
         btn: true,
         'btn-primary':
            !props.variant || props.variant === ButtonEnums.ButtonVariant.Cta,
         'btn-secondary': props.variant === ButtonEnums.ButtonVariant.Secondary,
         'btn-link':
            props.variant === ButtonEnums.ButtonVariant.Action ||
            props.variant === ButtonEnums.ButtonVariant.DestructiveAction,
         'btn-danger': props.variant === ButtonEnums.ButtonVariant.Destructive,
         destructive:
            props.variant === ButtonEnums.ButtonVariant.DestructiveAction,
         'btn-light': props.variant === ButtonEnums.ButtonVariant.Light,
      };
   } else {
      btnClass = {
         ...btnClass,
         chip: true,
         primary:
            !props.variant || props.variant === ButtonEnums.ButtonVariant.Cta,
         'primary-bordered': props.variant === ButtonEnums.ButtonVariant.Action,
         'secondary-bordered':
            props.variant === ButtonEnums.ButtonVariant.Secondary,
         danger: props.variant === ButtonEnums.ButtonVariant.Destructive,
         'danger-bordered':
            props.variant === ButtonEnums.ButtonVariant.DestructiveAction,
         light: props.variant === ButtonEnums.ButtonVariant.Light,
         clickable: props.disabled !== true,
      };
   }

   if (props.size) {
      if (!props.asChip) {
         btnClass['btn-sm'] = props.size === ButtonEnums.ButtonSize.Small;
         btnClass['btn-md'] = props.size === ButtonEnums.ButtonSize.Medium;
         btnClass['btn-lg'] = props.size === ButtonEnums.ButtonSize.Large;
      } else {
         const chipSize = SizesValuesMap[props.size as Sizes];
         btnClass[chipSize] = true;
      }
   }

   if (props.buttonClasses) {
      props.buttonClasses.split(' ').forEach((cls) => {
         btnClass[cls] = true;
      });
   }

   const hiddenText = props.hiddenText
      ? html`<span class="visually-hidden">${props.hiddenText}</span>`
      : nothing;

   return html`
      <button
         type=${ButtonTypeValues[props.type ?? ButtonEnums.ButtonType.Button]}
         aria-label="${ifDefined(props.buttonAriaSettings?.ariaLabel)}"
         aria-hidden="${ifDefined(props.buttonAriaSettings?.ariaHidden)}"
         aria-expanded=${ifDefined(props.buttonAriaSettings?.ariaExpanded)}
         aria-haspopup=${ifDefined(props.buttonAriaSettings?.ariaHasPopup)}
         role="${ifDefined(props.buttonAriaSettings?.role)}"
         title="${ifDefined(props.title)}"
         class="${classMap(btnClass)}"
         ?disabled="${props.disabled}"
         part="button text"
         data-bs-toggle="${props.dataBsToggle || nothing}"
         @click="${props?.onClick}"
      >
         ${props.iconName
            ? html`${AtlIconTemplate({
                 iconName: props.iconName,
                 iconClassNames: props.iconClassName,
                 iconSource: props.iconSource,
                 iconSet: props.iconSet,
                 ariaHidden: true,
              })}`
            : nothing}
         ${props.innerContent ? props.innerContent : nothing} ${hiddenText}
      </button>
   `;
}